import React, { useState, Fragment } from "react";
import { Layout, TripleLights, Ads } from "~/src/components";
import getContext from "context";
import {
  Marquee,
  Menu,
  Modal,
  Video,
  CardList,
  Button,
  Review
} from "planetyze-ui";
import {
  PageBlock,
  Image,
  FieldGroup,
  WishlistButton,
  MicroFormat
} from "./components";
import { Link } from "libs/router";
import { graphql } from "react-apollo";
import query from "./Guidebook.gql";
import "./Guidebook.scss";

const Guidebook = props => {
  const { isAuth, t, lang } = getContext();
  const [state, setState] = useState({
    isGalleryOpen: false,
    isVideoOpen: false
  });

  const { isGalleryOpen, isVideoOpen } = state;

  const toggleGallery = () => {
    setState({ isGalleryOpen: !isGalleryOpen });
  };

  const toggleVideo = () => {
    setState({ isVideoOpen: !isVideoOpen });
  };

  return (
    <Layout.Loader {...props.data}>
      {({ guidebook }) => {
        if (!guidebook) return null;
        const hasVideo = !!("video" in guidebook && guidebook.video);
        const hasGallery = !!(
          guidebook.gallery && guidebook.gallery.length > 1
        );

        const menu = getMenu(guidebook, t);

        const params = {
          guidebook: guidebook.title,
          location: guidebook.location.name,
          lead:
            guidebook.lead ||
            guidebook.description ||
            `${guidebook.title} in ${guidebook.location.name}`
        };

        return (
          <Layout
            title={t("seo.guidebook.title", params)}
            description={t("seo.guidebook.description", params)}
            pageType="sight"
            url={props.uri}
            location={guidebook.location.slug}
            headerMobileAds={guidebook.content ? true : false}
          >
            {/* Marquee */}
            <Guidebook.Marquee
              guidebook={guidebook}
              toggleGallery={toggleGallery}
              toggleVideo={toggleVideo}
              hasVideo={hasVideo}
              hasGallery={hasGallery}
              t={t}
            />

            <MicroFormat {...guidebook} lang={lang} />

            {/* Modal:Video */}
            {hasVideo && (
              <Modal isOpen={isVideoOpen} onChange={toggleVideo} t={t}>
                <Video code={guidebook.video.code} />
              </Modal>
            )}

            {/* Modal:Gallery */}
            {isGalleryOpen && (
              <Guidebook.GalleryModal
                guidebook={guidebook}
                isOpen={isGalleryOpen}
                toggleModal={toggleGallery}
                t={t}
              />
            )}

            <div className="container max-w-lg mx-auto pt-0">
              <Menu
                items={menu}
                className="hidden guidebook-menu"
                current="/"
                scroll
              />

              {/* Lead */}
              <div id="overview" className="text-xl leading-normal mb-16">
                {guidebook.lead}
              </div>

              {isAuth && guidebook.map_lat && (
                <div className="text-center mb-20">
                  <WishlistButton
                    id={guidebook.id}
                    isActive={guidebook.inWishlist}
                  />
                </div>
              )}

              {/* Information */}
              {guidebook.map_lat && (
                <Guidebook.Information guidebook={guidebook} t={t} />
              )}

              {/* Ads */}
              {guidebook.content && <Ads className="mb-10" />}

              {/* Gallery */}
              {hasGallery && (
                <Guidebook.Gallery
                  guidebook={guidebook}
                  toggleModal={toggleGallery}
                  t={t}
                />
              )}
              {/* Content */}
              {guidebook.content && (
                <Guidebook.Content content={guidebook.content} t={t} />
              )}
              {/* Access */}
              {guidebook.access && (
                <Guidebook.Access content={guidebook.access} t={t} />
              )}
              {/* Itineraries */}
              {guidebook.itineraries.nodes.length > 0 && (
                <Guidebook.Itineraries
                  items={guidebook.itineraries}
                  location={guidebook.location}
                  t={t}
                />
              )}

              {/* NearBy */}
              {guidebook.nearBy.nodes.length > 0 && (
                <Guidebook.NearBy items={guidebook.nearBy} t={t} />
              )}

              <TripleLights
                location={guidebook.location.slug}
                medium="sight"
                campaign={guidebook.url}
              />

              {/* Reviews */}
              {guidebook.reviews.nodes.length > 0 && (
                <Guidebook.Reviews items={guidebook.reviews} t={t} />
              )}
            </div>
          </Layout>
        );
      }}
    </Layout.Loader>
  );
};

const getMenu = (guidebook, t) => {
  const menu = [];
  menu.push({ label: t("guidebook.overview"), url: "#overview" });

  if (guidebook.gallery && guidebook.gallery.length > 1) {
    menu.push({ label: t("guidebook.photos-videos"), url: "#photos" });
  }

  if (guidebook.itineraries.nodes.length > 0) {
    menu.push({ label: t("guidebook.itineraries"), url: "#itineraries" });
  }

  if (guidebook.nearBy.nodes.length > 0) {
    menu.push({ label: t("guidebook.nearby"), url: "#nearby" });
  }

  if (guidebook.reviews.nodes.length > 0) {
    menu.push({ label: t("guidebook.reviews"), url: "#reviews" });
  }

  return menu;
};

Guidebook.GalleryModal = ({ guidebook, isOpen, toggleModal, t }) => {
  return (
    <Modal right isOpen={isOpen} onChange={toggleModal} t={t}>
      {guidebook.gallery.map(item => (
        <div className="mb-16" key={`picture-${item.id}`}>
          <img src={item.url} alt={item.caption} className="w-full" />
          {item.caption && (
            <div
              className="mt-4 text-lg leading-normal"
              dangerouslySetInnerHTML={{
                __html: item.caption
              }}
            />
          )}
        </div>
      ))}
    </Modal>
  );
};

Guidebook.Marquee = ({
  guidebook,
  hasVideo,
  toggleVideo,
  hasGallery,
  toggleGallery,
  t
}) => {
  const path = [
    { label: t("japan"), url: "/" },
    { label: guidebook.location.name, url: guidebook.location.url }
  ];

  return (
    <Marquee
      src={guidebook.image ? guidebook.image.url : null}
      title={guidebook.title}
      subtitle={guidebook.description}
      className="pb-32"
      path={path}
      video={hasVideo ? guidebook.video.code : null}
    >
      <div className="flex items-center  mt-6">
        {hasVideo && (
          <Button
            onClick={toggleVideo}
            className="mr-4"
            children={t("buttons.play-video")}
          />
        )}

        {hasGallery && (
          <div
            onClick={toggleGallery}
            className="text-white underline cursor-pointer"
          >
            {t("buttons.view-gallery")}
          </div>
        )}
      </div>
    </Marquee>
  );
};

Guidebook.Information = ({ guidebook, t }) => {
  return (
    <PageBlock>
      <div className="flex flex-wrap">
        <div className="w-full md:w-1/2">
          <iframe
            height="500"
            title="map"
            frameBorder="0"
            className="w-full border-0"
            src={`https://www.google.com/maps/embed/v1/place?q=${guidebook.map_lat},${guidebook.map_lng}&key=AIzaSyAK9G776j0foq2sTWwdEMgtESex0dXIYRE`}
          />
        </div>

        <div className="w-full md:w-1/2 pt-8 md:pl-6 md:pt-0">
          <FieldGroup
            title={t("guidebook.business-hours")}
            value={guidebook.business_hours}
          />

          <FieldGroup title={t("guidebook.price")} value={guidebook.price} />

          <FieldGroup
            title={t("guidebook.address")}
            value={guidebook.address}
          />

          <FieldGroup title={t("guidebook.phone")} value={guidebook.phone} />

          <FieldGroup title={t("guidebook.website")} value={guidebook.website}>
            <a
              href={guidebook.website}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline"
            >
              {guidebook.website}
            </a>
          </FieldGroup>
        </div>
      </div>
    </PageBlock>
  );
};

Guidebook.Gallery = ({ guidebook, toggleModal, t }) => {
  return (
    <PageBlock id="photos" title={t("guidebook.photos-videos")}>
      <div className="flex flex-wrap cursor-pointer" onClick={toggleModal}>
        {guidebook.gallery
          .slice(0, guidebook.gallery.length === 3 ? 2 : 4)
          .map(item => (
            <Image key={item.id} src={item.thumb} />
          ))}
        <div className="w-full text-center mt-4">
          <div className="text-primary underline hover:text-primary-dark cursor-pointer">
            {t("buttons.view-gallery")}
          </div>
        </div>
      </div>
    </PageBlock>
  );
};

Guidebook.Content = ({ content, t }) => {
  return (
    <PageBlock
      id="information"
      title={t("guidebook.about")}
      t={t}
      preview={content.length > 600}
    >
      <div
        id="root"
        className="blog-content"
        dangerouslySetInnerHTML={{
          __html: content.replace("<p>&nbsp;</p>", "")
        }}
      />
    </PageBlock>
  );
};

Guidebook.Access = ({ content, t }) => {
  return (
    <PageBlock
      id="access"
      title={t("guidebook.access")}
      preview={content.length > 600}
    >
      <div
        id="root"
        className="blog-content"
        dangerouslySetInnerHTML={{
          __html: content
            .replace("<p>&nbsp;</p>", "")
            .replace("<br /><br />", "")
        }}
      />
    </PageBlock>
  );
};

Guidebook.Itineraries = ({ items, location, t }) => {
  return (
    <PageBlock id="itineraries" title={t("blocks.related-itineraries")}>
      <Fragment>
        {items.nodes.map(item => (
          <CardList
            key={`itinerary-${item.id}`}
            title={item.title}
            description={item.description}
            url={item.url}
            src={item.image ? item.image.url : null}
            t={t}
          />
        ))}

        <Link
          to={`${location.url}/itineraries`}
          className="w-full text-center mt-4"
        >
          <div className="text-primary underline hover:text-primary-dark cursor-pointer">
            {t("buttons.view-all-location-itineraries", {
              location: location.name
            })}
          </div>
        </Link>
      </Fragment>
    </PageBlock>
  );
};

Guidebook.Reviews = ({ items, t }) => {
  return (
    <PageBlock id="reviews" title={t("guidebook.reviews")}>
      <Fragment>
        {items.nodes.map(item => (
          <Review key={`review-${item.id}`} {...item} t={t} />
        ))}
      </Fragment>
    </PageBlock>
  );
};

Guidebook.NearBy = ({ items, t }) => {
  return (
    <PageBlock id="nearby" title={t("guidebook.nearby")}>
      <Fragment>
        {items.nodes.map(item => (
          <CardList
            key={`itinerary-${item.id}`}
            title={item.title}
            description={item.description}
            url={item.url}
            src={item.image ? item.image.url : null}
            t={t}
          />
        ))}
      </Fragment>
    </PageBlock>
  );
};

export default graphql(query, {
  options: props => {
    return {
      variables: {
        guidebook: props.guidebook,
        location: props.slug
      }
    };
  }
})(Guidebook);
